import * as _ from "lodash";
import * as angular from "angular";

import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { PermissionCheck } from "../../shared/permission";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { Building } from "../../models/building.model";
import { House } from "../../models/house.model";
import { BuildingDataProvider } from "../../data-provider/building.dp";
import { HouseDataProvider } from "../../data-provider/house.dp";

let BuildingComponent = {
    selector: "vcViewBuilding",
    templateUrl: "/angular/components/building/building.component.html",
    bindings: {
        bid: "=",
    },
    controller: class BuildingController {
        public bid: any;
        public building: Building;
        public showMap: boolean = false;
        public buildingLoading: boolean = false;
        public buildingMarker: any;
        public map = {
            bounds: {},
            center: { latitude: 0, longitude: 0 },
            zoom: 3,
            control: {},
            options: { scrollwheel: false },
        };
        public locating: boolean | undefined = undefined;

        private $scope: any;
        private $timeout: any;
        private __app_type: string;
        public __env: any;
        private dialog: DialogUtils;
        private toast: ToastUtils;
        private modelsUtils: any;
        public permissionProvider: PermissionCheck;
        private dataProvider: BuildingDataProvider;
        private houseDataProvider: HouseDataProvider;

        public isLeaderView: boolean;
        public essentialsMode: boolean;
        public houses: Array<House>;
        public floorHouses: {};

        constructor(
            $scope: any,
            $timeout: object,
            __app_type: string,
            __env: any,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            ModelsUtils: any,
            PermissionCheck: PermissionCheck,
            BuildingDataProvider: BuildingDataProvider,
            HouseDataProvider: HouseDataProvider,
            CampaignDataProvider: CampaignDataProvider
        ) {
            this.$scope = $scope;
            this.$timeout = $timeout;
            this.__app_type = __app_type;
            this.__env = __env;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.modelsUtils = ModelsUtils;
            this.permissionProvider = PermissionCheck;
            this.dataProvider = BuildingDataProvider;
            this.houseDataProvider = HouseDataProvider;
            this.isLeaderView = this.__app_type == "LEADER";
            this.essentialsMode =
                CampaignDataProvider.activeCampaign.campaignstatusid == "5";
        }

        $onInit() {
            this.loadBuildingDetails();
            // $mdDialog.hide() resolves whichever dialog is topmost, so never
            // hand the building to it on a route change (AV-18111).
            this.$scope.$on("$routeChangeStart", () => this.dialog.cancel());
            this.$timeout(() => (this.showMap = true), 500);

            this.$scope.$watch(
                () => this.bid,
                (
                    newValue: string | number | undefined,
                    oldValue: string | number | undefined
                ) => {
                    this.buildingLoading = true;
                    if (oldValue && newValue != oldValue) {
                        this.loadBuildingDetails();
                    }
                }
            );
            this.loadHouses();
        }

        loadHouses(): void {
            this.houseDataProvider
                .building(this.bid)
                .then((houses: House[]) => {
                    this.houses = houses;
                    this.floorHouses =
                        this.houseDataProvider.sortHousesIntoFloors(
                            this.houses
                        );
                });
        }

        close(building: Building): void {
            this.dialog.hide(building);
        }

        openAccessTypeDialog(ev: object): void {
            this.dialog
                .show({
                    component: "vc-access-type",
                    targetEvent: ev,
                    scope: this.$scope,
                    locals: {
                        selectedAccessType: this.building.getAccessType(),
                    },
                })
                .then((accessType: string) => {
                    this.building.accessibility = accessType;
                    this.building.update();
                });
        }

        openBuildingHousesDialog(
            building: Building,
            houses: House[],
            ev: object
        ): void {
            this.dialog
                .show({
                    component: "vc-building-houses",
                    targetEvent: ev,
                    scope: this.$scope,
                    locals: {
                        building: building,
                        houses: houses,
                    },
                    size: "large",
                })
                .then((houses: House[]) => {
                    this.houses = houses;
                    this.floorHouses =
                        this.houseDataProvider.sortHousesIntoFloors(
                            this.houses
                        );
                });
        }

        openBuildingNoteDialog(ev: object): void {
            this.dialog
                .show({
                    component: "vc-house-note-form",
                    targetEvent: ev,
                    scope: this.$scope,
                    locals: {
                        houseNote: this.building.notes,
                    },
                })
                .then((notes: unknown) => {
                    if (typeof notes !== "string") return;
                    this.building.notes = notes;
                    this.building.update();
                });
        }

        openEditBuildingDialog(building: Building, ev: object): void {
            this.dialog
                .show({
                    component: "vc-house-form",
                    targetEvent: ev,
                    scope: this.$scope,
                    ariaLabel: "House",
                    locals: {
                        selectedHouse: this.building,
                    },
                    size: "x-large",
                })
                .then((data: object) => {
                    building.setData(data);
                    this.initialiseMap();
                });
        }

        openNewBuildingHouseDialog(ev: object): void {
            let newHouse = this.modelsUtils.house({
                bid: this.building.id,
                hnumber: this.building.hnumber || "",
                hname: this.building.hname || "",
                haddress: this.building.haddress,
                hcity: this.building.hcity,
                hstate: this.building.hstate,
                hzipcode: this.building.hzipcode || "",
                hprecinct: this.building.hprecinct || "",
                hlocationtype: this.building.hlocationtype,
                hlongitude: this.building.hlongitude,
                hlatitude: this.building.hlatitude,
            });
            this.dialog
                .show({
                    component: "vc-house-form",
                    targetEvent: ev,
                    ariaLabel: "House",
                    scope: this.$scope,
                    locals: {
                        selectedHouse: newHouse,
                    },
                    size: "x-large",
                })
                .then((house: House) => {
                    this.loadHouses();
                    this.houseDataProvider.addToFilteredHouses(house);
                });
        }

        loadBuildingDetails(): void {
            this.buildingLoading = true;
            this.dataProvider
                .getBuilding(this.bid)
                .then((building: Building) => {
                    this.buildingLoading = false;
                    this.building = building;
                    this.initialiseMap();
                });
        }

        initialiseMap(): void {
            if (
                this.building.hlatitude &&
                (this.building.hlatitude + "").trim() != "" &&
                this.building.hlongitude &&
                (this.building.hlongitude + "").trim() != ""
            ) {
                this.setHouseMarker({
                    coords: {
                        longitude: this.building.hlongitude,
                        latitude: this.building.hlatitude,
                    },
                });
            }
        }

        setHouseMarker(location?: any): void {
            if (!location) {
                location = { coords: { latitude: 0, longitude: 0 } };
                this.map.center = _.cloneDeep(location.coords);
                this.map.zoom = 1;
            } else {
                this.map.center = _.cloneDeep(location.coords);
                this.map.zoom = 16;
            }
            this.buildingMarker = {
                id: 1,
                coords: location.coords,
            };
        }
    },
};

angular
    .module("building")
    .component(BuildingComponent.selector, BuildingComponent);
